import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DeathData, formatDeathRelationship, formatDeathState, getDeathStateColor } from '@/domain/deathData/deathData'
import { ServiceType } from '@/domain/offerings/offering'
import { calculateAgeStr } from '@/utils/dates'
import { formatDocNumber } from '@/utils/helpers'
import { format } from 'date-fns'
import { Building2, Calendar, ClipboardList, Edit, IdCardIcon, PlusCircle, Stethoscope, Trash, User, UsersIcon } from 'lucide-react'


interface DeathDataListProps {
  deathData: DeathData[]
  onEdit?: (data: DeathData) => void
  onDelete?: (data: DeathData) => void
  onAdd?: () => void
  serviceType?: ServiceType
}

export const DeathDataList = ({ deathData, onEdit, onDelete, onAdd, serviceType }: DeathDataListProps) => {
  const canModify = serviceType !== ServiceType.PARCELAS
  return (
    <div className='flex flex-col gap-3'>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">
          Fallecidos
        </h3>
        {canModify && onAdd && (
          <Button variant="outline" size="sm" className="h-8" onClick={onAdd}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Agregar Fallecido
          </Button>
        )}
      </div>
      {deathData.length === 0 ? (
        <div className="text-center py-6 border rounded-xl border-dashed border-border/60 bg-muted/10">
          <p className="text-sm text-muted-foreground italic">No hay fallecidos registrados.</p>
        </div>
      ) : (
        deathData.filter(d => d.name).map((data) => (
          <div key={data.id} className="border rounded-xl p-5 bg-background shadow-sm hover:shadow-md transition-shadow border-border/60 group relative">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-full border border-primary/20">
                  <User className="h-5 w-5 text-primary flex-shrink-0" />
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg font-bold capitalize leading-tight">{data.name.toLowerCase()}</h4>
                    {data.state && (
                      <Badge className={`${getDeathStateColor(data.state)} py-0 px-2 h-5`}>
                        {formatDeathState(data.state)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-sm mt-0.5">
                    {data.docType && data.docNumber && (
                      <div className="flex items-center gap-1">
                        <IdCardIcon className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                        <span>{data.docType} {formatDocNumber(data.docNumber, data.docType)}</span>
                      </div>
                    )}
                    {data.birthDate && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                        <span>{calculateAgeStr(new Date(data.birthDate), new Date(data.deathDate ?? new Date()))}</span>
                      </div>
                    )}
                    {data.relationshipWithDeceased && (
                      <div className="flex items-center gap-1">
                        <UsersIcon className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                        <span>Parentesco: </span>
                        <span>{formatDeathRelationship(data.relationshipWithDeceased)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {canModify && (
                <div className="opacity-0 group-hover:opacity-100 transition-all duration-200 flex gap-1 bg-white/90 backdrop-blur-sm p-1 rounded-lg shadow-lg border absolute right-5 top-5 z-10">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:bg-blue-50 hover:text-blue-700" onClick={() => onEdit?.(data)}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  {onDelete && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => onDelete?.(data)}>
                      <Trash className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-evenly gap-4 mb-5 p-4 rounded-xl bg-muted/20 border border-border/40">
              {data.birthDate && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Nacimiento</span>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary/60 flex-shrink-0" />
                    <span className="text-sm font-medium">{format(new Date(data.birthDate), "dd/MM/yyyy")}</span>
                  </div>
                </div>
              )}
              {data.deathDate && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Fallecimiento</span>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary/60 flex-shrink-0" />
                    <span className="text-sm font-medium">{format(new Date(data.deathDate), "dd/MM/yyyy")}</span>
                  </div>
                </div>
              )}
              {data.cremationDate && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Cremación</span>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary/60 flex-shrink-0" />
                    <span className="text-sm font-medium">{format(new Date(data.cremationDate), "dd/MM/yyyy")}</span>
                  </div>
                </div>
              )}
              {data.mortuary && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Funeraria</span>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary/60 flex-shrink-0" />
                    <span className="text-sm font-medium capitalize">{data.mortuary?.toLowerCase()}</span>
                  </div>
                </div>
              )}
              {data.medicOfDeceased && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Médico</span>
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-primary/60 flex-shrink-0" />
                    <span className="text-sm font-medium capitalize">{data.medicOfDeceased?.toLowerCase()}</span>
                  </div>
                </div>
              )}
            </div>

            {(data.entryNumber || data.volume || data.leaf || data.year || data.office || data.deathCertificateAuthorization) && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardList className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-bold">Datos del Registro Civil</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {data.entryNumber && (
                    <div className="flex flex-col bg-muted/40 p-2 rounded-lg border border-border/30">
                      <span className="text-muted-foreground uppercase tracking-wider font-bold text-[10px]">Acta</span>
                      <span className="text-sm font-semibold">{data.entryNumber}</span>
                    </div>
                  )}
                  {data.volume && (
                    <div className="flex flex-col bg-muted/40 p-2 rounded-lg border border-border/30">
                      <span className="text-muted-foreground uppercase tracking-wider font-bold text-[10px]">Tomo</span>
                      <span className="text-sm font-semibold">{data.volume}</span>
                    </div>
                  )}
                  {data.leaf && (
                    <div className="flex flex-col bg-muted/40 p-2 rounded-lg border border-border/30">
                      <span className="text-muted-foreground uppercase tracking-wider font-bold text-[10px]">Folio</span>
                      <span className="text-sm font-semibold">{data.leaf}</span>
                    </div>
                  )}
                  {data.year && (
                    <div className="flex flex-col bg-muted/40 p-2 rounded-lg border border-border/30">
                      <span className="text-muted-foreground uppercase tracking-wider font-bold text-[10px]">Año</span>
                      <span className="text-sm font-semibold">{data.year}</span>
                    </div>
                  )}
                  {data.office && (
                    <div className="flex flex-col bg-muted/40 p-2 rounded-lg border border-border/30 col-span-2 sm:col-span-1">
                      <span className="text-muted-foreground uppercase tracking-wider font-bold text-[10px]">Oficina</span>
                      <span className="text-sm font-semibold line-clamp-1 capitalize" title={data.office?.toLowerCase()}>{data.office?.toLowerCase()}</span>
                    </div>
                  )}
                  {data.deathCertificateAuthorization && (
                    <div className="flex flex-col bg-muted/40 p-2 rounded-lg border border-border/30 col-span-2 sm:col-span-2">
                      <span className="text-muted-foreground uppercase tracking-wider font-bold text-[10px]">Autorización de Sepultura</span>
                      <span className="text-sm font-semibold line-clamp-1" title={data.deathCertificateAuthorization}>{data.deathCertificateAuthorization}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {data.notes && (<>
              <div className="border-t mt-4"></div>
              <div className="flex flex-col bg-muted/40 p-2 rounded-lg border border-border/30 mt-4">
                <span className="text-muted-foreground uppercase tracking-wider font-bold text-[10px]">Notas</span>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <p className="text-xs leading-relaxed">
                    {data.notes}
                  </p>
                </div>
              </div></>
            )}
          </div>
        )))}
    </div>
  )
}
